import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { formatYds } from "../../utility/installerCapacityRemaining";

// #150 (2026-05-21) — same FileMaker-protocol invoice link used in
// JobsDetailModal (`fmp://$/KellerInteriors.fmp12?script=PresentInvoice_
// (_invoiceNo_)&param=...`). Clicking the invoice# on a recommendation
// row opens the invoice in FileMaker (handled by the OS via the fmp://
// scheme). Mirrors JobsDetailModal.getInvoiceHref so a future FileMaker
// script-name change is a one-place edit; we should extract this to a
// shared utility if it diverges again. Click also stops the popover's
// outside-click handler from collapsing the popover.
export const getInvoiceHref = (invoiceNo) =>
  `fmp://$/KellerInteriors.fmp12?script=PresentInvoice_(_invoiceNo_)&param=${invoiceNo}`;

// #205 (Brandon, 2026-06-17) — format the estimated road miles for a
// recommendation row. `null`/non-finite (unmeasurable: candidate or assigned
// stop not geocoded) → "—" placeholder, per Leland (so a blank doesn't read as
// "0 miles"). Sub-mile → "<1"; under 10 → one decimal; else rounded whole.
export const formatStopMiles = (miles) => {
  if (miles === null || miles === undefined || !Number.isFinite(miles))
    return "—";
  if (miles < 0.95) return "<1";
  if (miles < 10) return miles.toFixed(1);
  return String(Math.round(miles));
};

// - - - - - - - - - -
// Two-section popover that renders the output of
// `recommendFillerCarpetJobs` for issue #140. Anchored to the clicked
// PartialCapacityIcon; closes on outside-click, Escape, or close-button.
//
// Visual structure:
//
//   ┌─────────────────────────────────────────────┐
//   │  Capacity Recommendations                 ✕ │  header band
//   │  <installer name> · <cellDate>              │
//   │  <remaining> yds remaining of <effectiveCap>│
//   │  (<N> carpet jobs · <U> yds used)           │
//   ├─────────────────────────────────────────────┤
//   │  🏃🏽‍♂️ Expedite — pull-forward candidates    │  section 1 label
//   │  ────                                       │
//   │  <row> <row> <row>                          │
//   ├─────────────────────────────────────────────┤
//   │  🔴 RTS — unassigned candidates             │  section 2 label
//   │  ────                                       │
//   │  <row> <row>                                │
//   └─────────────────────────────────────────────┘
//
// Read-only v1 — clicking a row does NOT initiate a move; the install
// manager closes the popover and re-assigns manually via drag-drop.
// Click-to-action is a documented v1.1 scope expansion.

const POPOVER_WIDTH = 420;
const POPOVER_MAX_HEIGHT = 480;

// - - - - - - - - - -
// Position the popover relative to the clicked icon's bounding rect.
// Default: anchored below the icon, right-aligned. Clamps to viewport so
// the popover never renders off-screen at edges of the grid.
const computePopoverPosition = (anchorRect, viewportWidth, viewportHeight) => {
  if (!anchorRect) return { left: 0, top: 0 };
  // Prefer below-and-aligned-right of the icon; flip above if not enough room.
  let left = anchorRect.right - POPOVER_WIDTH;
  let top = anchorRect.bottom + 4;
  // Right-edge clamp
  if (left + POPOVER_WIDTH > viewportWidth - 8) {
    left = viewportWidth - POPOVER_WIDTH - 8;
  }
  // Left-edge clamp (icons near the start-of-grid)
  if (left < 8) left = 8;
  // Bottom-edge flip
  if (top + POPOVER_MAX_HEIGHT > viewportHeight - 8) {
    const flipped = anchorRect.top - POPOVER_MAX_HEIGHT - 4;
    if (flipped >= 8) {
      top = flipped;
    } else {
      // Both directions too tight — anchor to top of viewport with vertical
      // scroll fallback. Better to be visible-with-scroll than off-screen.
      top = 8;
    }
  }
  return { left, top };
};

// Row renderer — shared between the two tier sections to keep their visual
// language identical. The tier-specific badge is rendered by the section
// wrapper, not here.
const FillerRow = ({ entry }) => {
  const customer =
    `${entry.customerFirstName} ${entry.customerLastName}`.trim() ||
    "(no name)";
  const isExpedite = entry.expedite;
  // Furniture / steps piece counts are added 2026-05-19 (UAT review #2).
  // Only show the badge when the job actually contains pieces of that type
  // (e.g. don't render "0 furniture" — it's just noise for furniture-free
  // jobs, which is most of them).
  const furniturePieces = entry.furniturePieces || 0;
  const stepPieces = entry.stepPieces || 0;
  return (
    <div
      data-testid="filler-row"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        padding: "8px 12px",
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "8px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>
          {/* #150 — clickable invoice# opens the invoice in FileMaker
              via the same fmp:// link used in JobsDetailModal. Click
              propagation must NOT bubble to the popover's outside-click
              handler. target="_blank" + rel="noreferrer" mirror the
              JobsDetailModal pattern. */}
          <a
            href={getInvoiceHref(entry.invoiceNo)}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              color: "inherit",
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              cursor: "pointer",
            }}
          >
            #{entry.invoiceNo}
          </a>{" "}
          · {customer}
          {/* #173 — surface type tag for hard-surface rows (e.g. "Tile",
              "Floating") so the manager knows which capacity bucket the job
              fills. Carpet rows have no surfaceType → no tag. */}
          {entry.surfaceType && entry.surfaceType !== "Repair" && (
            <span style={{ marginLeft: "6px", fontSize: "0.7rem", fontWeight: 600, opacity: 0.7 }}>
              [{entry.surfaceType}]
            </span>
          )}
        </span>
        <span style={{ fontWeight: 700, fontSize: "0.875rem", whiteSpace: "nowrap" }}>
          {formatYds(entry.carpetYds)} {entry.unit || "yds"}
          {furniturePieces > 0 && (
            <span style={{ marginLeft: "8px", opacity: 0.7, fontWeight: 600 }}>
              · 🪑 {formatYds(furniturePieces)}
            </span>
          )}
          {stepPieces > 0 && (
            <span style={{ marginLeft: "8px", opacity: 0.7, fontWeight: 600 }}>
              · 🪜 {formatYds(stepPieces)}
            </span>
          )}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: "10px",
          fontSize: "0.75rem",
          opacity: 0.75,
          flexWrap: "wrap",
        }}
      >
        <span>📍 {entry.location || "—"}</span>
        {entry.storeNo && <span>🏪 Store {entry.storeNo}</span>}
        {/* #205 (Brandon, 2026-06-17; updated 2026-06-17) — estimated road
            distance from the installer's nearest already-assigned stop that day
            to this candidate (straight-line × 1.3 road factor — the same number
            the Stop Distance gate uses). HIDDEN entirely when unmeasurable
            (candidate or assigned stop not geocoded yet) — Brandon read the old
            "🚗 — mi" placeholder as broken, and with geocoding still backfilling
            most rows are unmeasurable, so we only show the badge when there's a
            real number. */}
        {Number.isFinite(entry.stopMiles) && (
          <span
            title="Estimated road distance from the installer's nearest assigned stop that day (straight-line × 1.3)"
          >
            🚗 {formatStopMiles(entry.stopMiles)} mi
          </span>
        )}
        {isExpedite && entry.dateScheduled && (
          <span>📅 currently {entry.dateScheduled}</span>
        )}
        {isExpedite && entry.installer && (
          <span>👤 {entry.installer}</span>
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ icon, label, count }) => (
  <div
    style={{
      padding: "8px 12px 4px 12px",
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      opacity: 0.85,
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    }}
  >
    <span style={{ marginRight: "6px" }}>{icon}</span>
    {label}
    <span style={{ marginLeft: "6px", opacity: 0.7 }}>({count})</span>
  </div>
);

const EmptyTierMessage = ({ message }) => (
  <div
    style={{
      padding: "8px 12px 12px 12px",
      fontSize: "0.8rem",
      opacity: 0.6,
      fontStyle: "italic",
    }}
  >
    {message}
  </div>
);

// - - - - - - - - - -
// #175 — surface summary blocks extracted as components so the header band can
// render carpet-only, hard-surface-only, OR (Option B, does-both installers)
// BOTH stacked. `label` is shown only in the both-surfaces case to tell the
// two blocks apart; `showStops` suppresses the per-day job/repair-stop lines on
// the second block so the shared (surface-agnostic) stop counts aren't printed
// twice.

const SummaryLabel = ({ label }) =>
  label ? (
    <div style={{ fontWeight: 700, opacity: 0.85, marginTop: "2px" }}>{label}</div>
  ) : null;

const StopsLines = ({ info }) => (
  <>
    {/* #146 — stops hard caps. Render only when the cap is configured (a
        Brandon-populated value is present on the doc), independent of the value
        itself, so a configured "0" still surfaces visibly. */}
    {info.isStopsJobsCapConfigured && (
      <div>
        📋 {formatYds(info.remainingJobStops)} job stops remaining of{" "}
        {formatYds(info.effectiveStopsJobsCap)} · {formatYds(info.usedJobStops)} used
      </div>
    )}
    {info.isStopsRepairsCapConfigured && (
      <div>
        🔧 {formatYds(info.remainingRepairStops)} repair stops remaining of{" "}
        {formatYds(info.effectiveStopsRepairsCap)} · {formatYds(info.usedRepairStops)} used
      </div>
    )}
  </>
);

const HardSurfaceSummary = ({ info, label, showStops = true }) => {
  if (!info) return null;
  return (
    <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
      <SummaryLabel label={label} />
      {/* #173 — per-type hard-surface capacity summary (sqft). One line per
          configured type that has room for another job. */}
      {(info.perType || [])
        .filter((pt) => pt.remaining > 0)
        .map((pt) => (
          <div key={pt.type}>
            {pt.type}: next job up to {formatYds(pt.remaining)} sqft ·{" "}
            {pt.count} job{pt.count === 1 ? "" : "s"} ·{" "}
            {formatYds(pt.usedSqft)} sqft used
          </div>
        ))}
      {showStops && <StopsLines info={info} />}
    </div>
  );
};

const CarpetSummary = ({ info, label, showStops = true }) => {
  if (!info) return null;
  return (
    <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
      <SummaryLabel label={label} />
      {/*
        #167 (2026-05-29): under the per-stop cap + daily-budget model,
        `remaining` is the daily budget left over. "Next job: up to X yds" still
        reads correctly because a single recommended job CAN be up to that
        remaining budget. Used-yds + job count shown for context.
      */}
      <div>
        Next job: up to {formatYds(info.remaining)} yds · {info.carpetJobCount}{" "}
        carpet job{info.carpetJobCount === 1 ? "" : "s"} ·{" "}
        {formatYds(info.usedYds)} yds used
      </div>
      {info.effectiveFurnitureCap > 0 && (
        <div>
          🪑 Next job: up to {formatYds(info.remainingFurniture)} furniture ·{" "}
          {formatYds(info.usedFurniture)} used
        </div>
      )}
      {info.effectiveStepsCap > 0 && (
        <div>
          🪜 Next job: up to {formatYds(info.remainingSteps)} steps ·{" "}
          {formatYds(info.usedSteps)} used
        </div>
      )}
      {showStops && <StopsLines info={info} />}
    </div>
  );
};

export const RecommendedFillersPopover = ({
  open,
  anchorRect,
  onClose,
  installerName,
  cellDate,
  capacityInfo,
  recommendations,
}) => {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const expediteJobs = recommendations?.expediteJobs || [];
  const rtsJobs = recommendations?.rtsJobs || [];
  const alreadyScheduledJobs = recommendations?.alreadyScheduledJobs || [];
  const repairJobs = recommendations?.repairJobs || [];

  const { left, top } = computePopoverPosition(
    anchorRect,
    window.innerWidth,
    window.innerHeight
  );

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Recommended filler jobs"
      data-testid="recommended-fillers-popover"
      style={{
        position: "fixed",
        left,
        top,
        width: `${POPOVER_WIDTH}px`,
        maxHeight: `${POPOVER_MAX_HEIGHT}px`,
        zIndex: 9999,
        backgroundColor: "rgb(var(--color-base))",
        color: "rgb(var(--color-text-primary))",
        border: "1px solid rgba(89, 89, 89, 0.4)",
        borderRadius: "6px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // Counter-rotate the OptimizerGrid's red-tint inset on the parent
        // cell — without this the popover inherits the red wash via opacity
        // overlays. createPortal anchors us to document.body so we're outside
        // the cell anyway, but the z-index makes us a fresh stacking context.
      }}
    >
      {/* Header band */}
      <div
        style={{
          padding: "10px 12px 10px 12px",
          borderBottom: "1px solid rgba(89, 89, 89, 0.3)",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>
            Capacity Recommendations
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="recommended-fillers-popover-close"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "1rem",
              padding: "2px 6px",
              opacity: 0.65,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: "0.8rem", opacity: 0.85 }}>
          {installerName || "(installer)"} · {cellDate}
        </div>
        {/* #175 — surface summary. Option B: a does-both installer shows BOTH
            a carpet block and a hard-surface block (each labeled); the shared
            per-day stop lines render once (on the carpet block) to avoid
            duplication. Single-surface installers render just their one block,
            unlabeled, exactly as before. */}
        {capacityInfo && capacityInfo.isBothSurfaces && (
          <>
            <CarpetSummary info={capacityInfo.carpetInfo} label="Carpet" />
            <HardSurfaceSummary
              info={capacityInfo.hardInfo}
              label="Hard Surface"
              showStops={false}
            />
          </>
        )}
        {capacityInfo &&
          !capacityInfo.isBothSurfaces &&
          capacityInfo.isHardSurface && <HardSurfaceSummary info={capacityInfo} />}
        {capacityInfo &&
          !capacityInfo.isBothSurfaces &&
          !capacityInfo.isHardSurface && <CarpetSummary info={capacityInfo} />}
      </div>

      {/* Scrollable body */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {/* Tier 1 — Expedite */}
        <SectionHeader
          icon="🏃🏽‍♂️"
          label="Expedite — pull-forward candidates"
          count={expediteJobs.length}
        />
        {expediteJobs.length === 0 ? (
          <EmptyTierMessage message="No future expedite-flagged flooring jobs at this location that fit the remaining capacity." />
        ) : (
          expediteJobs.map((entry) => (
            <FillerRow key={`exp-${entry._id || entry.invoiceNo}`} entry={entry} />
          ))
        )}

        {/* Tier 2 — RTS */}
        <SectionHeader
          icon="🔴"
          label="RTS — unassigned candidates"
          count={rtsJobs.length}
        />
        {rtsJobs.length === 0 ? (
          <EmptyTierMessage message="No RTS flooring jobs at this installer's eligible locations that fit the remaining capacity." />
        ) : (
          rtsJobs.map((entry) => (
            <FillerRow key={`rts-${entry._id || entry.invoiceNo}`} entry={entry} />
          ))
        )}

        {/* Tier 4 — Already Scheduled Pull Forward (issue #156, 2026-05-22).
            Non-expedite carpet Jobs at active statuses (1.x, 2.x, 4) — but
            NOT 3 (= Tier 2's domain) — with a future date_scheduled. Use
            case: FAV auto-pre-scheduled docs at status 1.2 with tentative
            date and unassigned installer, ready to be claimed when material
            is in. */}
        <SectionHeader
          icon="📅"
          label="Already Scheduled — pull-forward candidates"
          count={alreadyScheduledJobs.length}
        />
        {alreadyScheduledJobs.length === 0 ? (
          <EmptyTierMessage message="No future-dated non-expedite flooring jobs that fit the remaining capacity." />
        ) : (
          alreadyScheduledJobs.map((entry) => (
            <FillerRow
              key={`asc-${entry._id || entry.invoiceNo}`}
              entry={entry}
            />
          ))
        )}

        {/* Tier 3 — Open Repairs (issue #145, 2026-05-20). Repair Orders
            assigned to this installer that are still open (statusCode 30).
            Synthetic carpetYds = 1; always fits when remaining > 0. */}
        <SectionHeader
          icon="🔧"
          label="Repairs — open repair orders"
          count={repairJobs.length}
        />
        {repairJobs.length === 0 ? (
          <EmptyTierMessage message="No open repair orders assigned to this installer." />
        ) : (
          repairJobs.map((entry) => (
            <FillerRow key={`rep-${entry._id || entry.invoiceNo}`} entry={entry} />
          ))
        )}
      </div>
    </div>,
    document.body
  );
};

// Exported for direct testing — pure helper, no React.
export { computePopoverPosition };

export default RecommendedFillersPopover;
